import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useStageFreshStrawEmbryosMutation } from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { StringMapDropdown } from '@/components/common/string-map-dropdown';
import {
  StringMapAttributeName,
  StringMapObjectTypeCode,
} from '@/components/common/string-map-dropdown/constants';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';

export interface FreshStrawStageEmbryosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: FreshStrawSession;
}

export function FreshStrawStageEmbryosDialog({
  open,
  onOpenChange,
  session,
}: FreshStrawStageEmbryosDialogProps) {
  const dispatch = useDispatch();

  const [fertId, setFertId] = useState('');
  const [fertName, setFertName] = useState('');
  const [fertDate, setFertDate] = useState('');
  const [calfType, setCalfType] = useState<number | null>(null);
  const [loadDate, setLoadDate] = useState('');
  const [count, setCount] = useState('');

  const [stageEmbryos, { isLoading }] = useStageFreshStrawEmbryosMutation();

  useEffect(() => {
    if (open) {
      setFertId('');
      setFertName('');
      setFertDate('');
      setCalfType(null);
      setLoadDate('');
      setCount('');
    }
  }, [open]);

  const sessionReady =
    !!session.transferDate && !!session.transferSite && !!session.fertSite;

  const canSubmit =
    sessionReady &&
    fertId.trim() !== '' &&
    fertDate !== '' &&
    calfType !== null &&
    loadDate.trim() !== '' &&
    count.trim() !== '';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const countNum = Number.parseInt(count, 10);
    const loadDateNum = Number.parseInt(loadDate, 10);
    if (Number.isNaN(countNum) || countNum <= 0) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Invalid count',
          detail: 'Count must be a positive whole number.',
          life: ToastLife.LONG,
        }),
      );
      return;
    }
    if (Number.isNaN(loadDateNum)) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Invalid load date',
          detail: 'Load date must be a whole number.',
          life: ToastLife.LONG,
        }),
      );
      return;
    }
    try {
      const response = await stageEmbryos({
        fertId,
        fertName: fertName || undefined,
        fertDate,
        fertSiteId: session.fertSite,
        transferDate: session.transferDate,
        transferSiteId: session.transferSite,
        farmLocationId: session.locationId || undefined,
        accountId: session.accountId || undefined,
        freshShipmentId: session.freshShipmentId || undefined,
        calfType: calfType!,
        loadDate: loadDateNum,
        freshShippedNum: countNum,
      }).unwrap();

      if (response.skipped) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'Already staged',
            detail:
              response.skippedReason ??
              'Staging rows already exist for this fertilization.',
            life: ToastLife.LONG,
          }),
        );
      } else {
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Embryos staged',
            detail: `Created ${response.staged} staging row${response.staged === 1 ? '' : 's'}.`,
            life: ToastLife.SHORT,
          }),
        );
      }
      onOpenChange(false);
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Stage failed',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Stage fresh embryos for import</DialogTitle>
          <DialogDescription>
            Seeds blank staging rows for a fertilization so the fresh-embryo
            offline upload has rows to match on embryoid. Transfer date, site,
            and other session fields are pulled from the filter panel.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {!sessionReady && (
            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              Set transfer date, transfer site, and fertilization site in the
              filter panel before staging embryos.
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stage-fert-id">Fertilization ID</Label>
            <Input
              id="stage-fert-id"
              value={fertId}
              onChange={(e) => setFertId(e.target.value)}
              placeholder="GUID"
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stage-fert-name">
              Fert name{' '}
              <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="stage-fert-name"
              value={fertName}
              onChange={(e) => setFertName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stage-fert-date">Fert date</Label>
            <Input
              id="stage-fert-date"
              type="date"
              value={fertDate}
              onChange={(e) => setFertDate(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Calf type</Label>
            <StringMapDropdown
              attributeName={StringMapAttributeName.CALF_TYPE}
              objectTypeCode={StringMapObjectTypeCode.CALF_TYPE}
              value={calfType ?? undefined}
              onChange={(e) =>
                setCalfType(typeof e.value === 'number' ? e.value : null)
              }
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stage-load-date">Load date (int)</Label>
            <Input
              id="stage-load-date"
              type="number"
              value={loadDate}
              onChange={(e) => setLoadDate(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stage-count"># of embryos to stage</Label>
            <Input
              id="stage-count"
              type="number"
              min={1}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Staging…' : 'Stage embryos'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
